import './DashboardDataTable.css';

import { ArrowDown, ArrowUp } from 'lucide-react';

import { Button, TableHead, TableRow, cn } from '@shigo-ui/ui';

import {
  DashboardDataTableSortDirection,
  type DashboardDataTableColumn,
  type DashboardDataTableSortProps,
  type DashboardDataTableSortState,
} from './DashboardDataTable.types';
import {
  isDashboardDataTableSelectionColumn,
  dashboardDataTableStickyCellClassName,
  dashboardDataTableStickyCellStyle,
} from './DashboardDataTableSticky';

export function DashboardDataTableHeader<TItem>({
  columns,
  sort,
  pinHeader = false,
  tinted = false,
}: {
  columns: DashboardDataTableColumn<TItem>[];
  sort: DashboardDataTableSortProps | undefined;
  pinHeader?: boolean | undefined;
  /** Page-tone column row, used when the table has a header inside its card. */
  tinted?: boolean | undefined;
}) {
  return (
    <TableRow className="border-border hover:bg-transparent">
      {columns.map((column) => {
        const activeDirection = dashboardDataTableActiveSortDirection(column, sort);

        return (
          <TableHead
            key={column.id}
            data-selection-column={isDashboardDataTableSelectionColumn(column.id) || undefined}
            aria-sort={dashboardDataTableAriaSort(activeDirection)}
            className={cn(
              'text-muted-foreground h-10 px-2 text-xs font-medium first:pl-4 last:pr-4',
              column.headerClassName,
              dashboardDataTableStickyCellClassName(column, {
                header: true,
                pinHeader,
              }),
              tinted &&
                'bg-muted text-text-tertiary [&>button]:text-text-tertiary h-auto py-2 [&_span]:overflow-visible [&_span]:text-clip [&>button]:h-6 [&>button]:max-w-none [&>button]:overflow-visible',
            )}
            style={dashboardDataTableStickyCellStyle(column)}
          >
            <DashboardDataTableHeaderContent
              activeDirection={activeDirection}
              column={column}
              sort={sort}
            />
          </TableHead>
        );
      })}
    </TableRow>
  );
}

function DashboardDataTableHeaderContent<TItem>({
  activeDirection,
  column,
  sort,
}: {
  activeDirection: DashboardDataTableSortState['direction'] | undefined;
  column: DashboardDataTableColumn<TItem>;
  sort: DashboardDataTableSortProps | undefined;
}) {
  const sortKey = column.sortKey ?? column.id;

  if (isDashboardDataTableSelectionColumn(column.id)) {
    return <div className="flex items-center justify-center">{column.header}</div>;
  }

  if (!sort || !column.sortable) {
    return <>{column.header}</>;
  }

  return (
    <Button
      type="button"
      variant="ghost"
      className="-ml-2 h-8 max-w-full gap-1 overflow-hidden px-2 text-xs font-medium hover:bg-transparent"
      onClick={() => {
        sort.onChange(nextSortState(sort.value, sortKey));
      }}
    >
      <span className="min-w-0 truncate">{column.header}</span>
      <DashboardDataTableSortIcon direction={activeDirection} />
    </Button>
  );
}

function DashboardDataTableSortIcon({
  direction,
}: {
  direction: DashboardDataTableSortState['direction'] | undefined;
}) {
  const className = 'h-3.5 w-3.5 shrink-0';
  if (direction === DashboardDataTableSortDirection.Asc) {
    return <ArrowUp className={className} aria-hidden="true" />;
  }
  if (direction === DashboardDataTableSortDirection.Desc) {
    return <ArrowDown className={className} aria-hidden="true" />;
  }
  return null;
}

function nextSortState(
  current: DashboardDataTableSortState | undefined,
  key: string,
): DashboardDataTableSortState | undefined {
  if (current?.key !== key) {
    return dashboardDataTableSortState(key, DashboardDataTableSortDirection.Asc);
  }

  if (current.direction === DashboardDataTableSortDirection.Asc) {
    return dashboardDataTableSortState(key, DashboardDataTableSortDirection.Desc);
  }

  return undefined;
}

function dashboardDataTableSortState(
  key: string,
  direction: DashboardDataTableSortDirection,
): DashboardDataTableSortState {
  return { key, direction };
}

function dashboardDataTableActiveSortDirection<TItem>(
  column: DashboardDataTableColumn<TItem>,
  sort: DashboardDataTableSortProps | undefined,
) {
  const sortKey = column.sortKey ?? column.id;
  return sort?.value?.key === sortKey ? sort.value.direction : undefined;
}

function dashboardDataTableAriaSort(
  direction: DashboardDataTableSortState['direction'] | undefined,
) {
  if (direction === DashboardDataTableSortDirection.Asc) {
    return 'ascending';
  }
  if (direction === DashboardDataTableSortDirection.Desc) {
    return 'descending';
  }
  return undefined;
}
